import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { AddToastNotification } from '~/constants/mutations';

import auth0Client from '~/plugins/authClient';

import router from '~/router';

const BASE_URL = process.env.VUE_APP_ARCANE_WEB_API_URL;

export default class ArcaneApiGateway {
  instance = null;
  token = null;

  constructor({ $auth0, $store }) {
    this.instance = axios.create({
      baseURL: BASE_URL,
    });

    this.instance.interceptors.request.use(
      async (config) => {
        try {
          this.instance.token = await $auth0.getAccessTokenSilently();
          config.headers.Authorization = `Bearer ${this.instance.token}`;
        } catch (error) {
          console.error('Error getting access token:', error);
          await auth0Client.loginWithRedirect({
            appState: { targetUrl: router.currentRoute.value.fullPath },
          });
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response Interceptor
    this.instance.interceptors.response.use(
      (response) => {
        // Handle successful responses
        return response;
      },
      (error) => {
        // Handle errors globally
        let toastErrorMessage = error.message;
        if (error.response && error.response.status === 403) {
          toastErrorMessage = ` Route ${error.config.url} is forbidden`;
        } else if (error.code === 'ERR_NETWORK') {
          toastErrorMessage = `${error.message} or route ${error.config.url} not found. Check also CORS policy`;
        }

        if (error?.response?.status !== 500) {
          $store.commit(`notificationStore/${AddToastNotification}`, {
            type: 'toast',
            title: toastErrorMessage,
            notification: { externalId: uuidv4() },
            createdAt: Date.now(),
          });
        }

        // Optionally, handle retries or throw a custom error
        return Promise.reject(error);
      },
    );
  }

  async post(endpoint, data) {
    const { url } = endpoint;
    return this.instance.post(url, data);
  }

  async get(endpoint, data) {
    const { url } = endpoint;
    return this.instance.get(url, data);
  }

  async delete(endpoint, data) {
    const { url } = endpoint;
    return this.instance.delete(url, data);
  }

  async patch(endpoint, data) {
    const { url } = endpoint;
    return this.instance.patch(url, data);
  }

  async put(endpoint, data, opts) {
    const { url } = endpoint;
    return this.instance.put(url, data, opts);
  }

  async putKeepAlive(endpoint, data) {
    const { url } = endpoint;

    return await fetch(`${BASE_URL}${url}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.instance.token}` },
      body: JSON.stringify(data), // keepalive: true,
    });
  }
}
